import { isAcceptableAccountNumber } from "@/utils/eecs";
import * as validators from "@vuelidate/validators";
import { ComputedRef, Ref } from "vue";
import { Composer } from "vue-i18n"; // import the Composer type

// Function to validate Estonian Personal ID checksum
const isValidEstonianPersonalIDChecksum = (value: string) => {
  if (!value || value.length !== 11) return false;

  const weightsFirstRound = [1, 2, 3, 4, 5, 6, 7, 8, 9, 1];
  const weightsSecondRound = [3, 4, 5, 6, 7, 8, 9, 1, 2, 3];

  let sum = 0;

  // Calculate the checksum using the first set of weights
  for (let i = 0; i < 10; i++) {
    sum += parseInt(value[i], 10) * weightsFirstRound[i];
  }

  let checkNumber = sum % 11;

  // If the modulo is 10, we use the second set of weights
  if (checkNumber === 10) {
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(value[i], 10) * weightsSecondRound[i];
    }
    checkNumber = sum % 11;

    // If the modulo is still 10, we set the check number to 0
    if (checkNumber === 10) {
      checkNumber = 0;
    }
  }

  // The check number must match the last digit of the ID
  return checkNumber === parseInt(value[10], 10);
};

// Validator for Estonian Personal ID (Isikukood)
const isValidEstonianPersonalID = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  const pattern = /^[1-6][0-9]{2}[0,1][0-9][0-3][0-9]{5}$/;
  return pattern.test(value) && isValidEstonianPersonalIDChecksum(value);
};

const isValidLatvianPersonalId = (idCode: string) => {
  const pattern = /^[0-3][0-9][01][0-9]{3}-[0-9]{5}$/;

  return pattern.test(idCode) && isValidLatvianPersonalIDChecksum(idCode);
};

const isValidLatvianPersonalIDChecksum = (idCode: string) => {
  const weights = [1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
  const digits = idCode.replace("-", "");
  let total = 0;
  for (let i = 0; i < 10; i++) {
    total += parseInt(digits[i]) * weights[i];
  }
  const checkDigit = ((1101 - total) % 11) % 10;
  return parseInt(digits[10]) === checkDigit;
};

const isValidLithuanianPersonalID = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  return /^\d{11}$/.test(value); // Basic 11-digit validation to start with
};
// Validator for Estonian Company Registration Code (Registrikood)
const isValidEstonianCompanyCode = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  return /^[1-9][0-9]{7}$/.test(value);
};

// Validator for Latvian Company Registration Number (URN)
const isValidLatvianCompanyNumber = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  return /^\d{11}$/.test(value);
};

// Validator for Lithuanian Company Registration Number
const isValidLithuanianCompanyNumber = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  return /^\d{9}$/.test(value);
};

const isReasonableDate = (dateString: string) => {
  const date = new Date(dateString);
  const startDate = new Date("1990-01-01");
  const now = new Date();

  if (isNaN(date.getTime())) {
    // date.getTime() is NaN if the date is invalid
    return false;
  }

  return date >= startDate && date <= now;
};

export const isValidIban = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  return smellsLikeIban(value) && validateIbanChecksum(value);
};

function smellsLikeIban(str: string) {
  return /^([A-Z]{2}[ -]?[0-9]{2})(?=(?:[ -]?[A-Z0-9]){9,30}$)((?:[ -]?[A-Z0-9]{3,5}){2,7})([ -]?[A-Z0-9]{1,3})?$/.test(
    str
  );
}

// FROM https://stackoverflow.com/questions/44656264/iban-regex-design
function validateIbanChecksum(iban: string) {
  const ibanStripped = iban
    .replace(/[^A-Z0-9]+/gi, "") //keep numbers and letters only
    .toUpperCase(); //calculation expects upper-case
  const m = ibanStripped.match(/^([A-Z]{2})([0-9]{2})([A-Z0-9]{9,30})$/);
  if (!m) return false;

  const numbericed = (m[3] + m[1] + m[2]).replace(/[A-Z]/g, function (ch) {
    //replace upper-case characters by numbers 10 to 35
    return (ch.charCodeAt(0) - 55).toString();
  });
  //The resulting number would be to long for javascript to handle without loosing precision.
  //So the trick is to chop the string up in smaller parts.

  const match = numbericed.match(/\d{1,7}/g);
  if (match === null) {
    return false;
  }
  const mod97 = match.reduce(function (total, curr) {
    return Number(total + curr) % 97;
  }, 0);

  return mod97 === 1;
}

const isValidEstonianVATNumber = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  return /^EE[0-9]{9}$/.test(value);
};
const isValidLatvianVATNumber = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  return /^LV[0-9]{11}$/.test(value);
};
const isValidLithuanianVATNumber = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  // Lithuanian VAT numbers can be 9 or 12 digits long lol
  return /^LT[0-9]{9,12}$/.test(value);
};

/**
 * Validates a Swedish VAT code
 *
 * Swedish VAT numbers follow the format SE + 10 digits + 01
 * The first 10 digits are the organization number
 * The last 2 digits are always "01"
 *
 * @param vatCode The VAT code to validate
 * @returns True if the VAT code is valid, false otherwise
 */
function isValidSwedishVATNumber(vatCode: string): boolean {
  if (!vatCode) return true; // consider empty values as valid. Use in combination with required
  const cleanVat = vatCode.replace(/\s/g, "").toUpperCase();

  const vatRegex = /^SE(\d{12})$/;
  if (!vatRegex.test(cleanVat)) {
    return false;
  }

  const digits = cleanVat.substring(2);

  if (!digits.endsWith("01")) {
    return false;
  }

  const orgNumber = digits.substring(0, 10);
  return verifyLuhnChecksum(orgNumber);
}

const namePattern =
  /^[A-Za-z\u00C0-\u00FF\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF]{2,}(?:[\s-]+[A-Za-z\u00C0-\u00FF\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF]{2,})+$/;

const phonePattern =
  /^(?:\+\d{1,3}|0\d{1,3}|00\d{1,2})?(?:\s?\(\d+\))?(?:[-/\s.]|\d)+$/;

const estonianDeviceEICPattern = /^38ZEE-\d{8}-[A-Z0-9]$/;

const isValidSwedishPersonalID = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required

  // Format can be YYYYMMDD-XXXX or YYMMDD-XXXX
  // also can be YYMMDD+XXXX, but gpt does not support 100+ year old people
  // full ID code will still work with the format of YYYYMMDD-XXXX
  const pattern = /^(\d{6}|\d{8})(-|\+)?\d{4}$/;
  if (!pattern.test(value)) return false;

  // Remove the hyphen
  const digits = value.replace("-", "").replace("+", "");

  // For 6-digit birth dates, determine century prefix
  let fullDigits;
  if (digits.length === 10) {
    const year = parseInt(digits.substring(0, 2));
    // If year is greater than current year's last 2 digits, assume 19xx, otherwise 20xx
    const currentYear = new Date().getFullYear() % 100;
    fullDigits = (year > currentYear ? "19" : "20") + digits;
  } else {
    fullDigits = digits;
  }

  // Validate date
  const year = parseInt(fullDigits.substring(0, 4));
  const month = parseInt(fullDigits.substring(4, 6));
  const day = parseInt(fullDigits.substring(6, 8));
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return false;
  }

  return verifyLuhnChecksum(digits);
};

function verifyLuhnChecksum(digits: string) {
  if (digits.length < 10) {
    return false;
  }
  digits = digits.replace("-", "").replace("+", "");
  let sum = 0;
  let start = 2;
  if (digits.length === 10) {
    start = 0;
  }

  for (let i = start; i < digits.length - 1; i++) {
    let digit = parseInt(digits[i]);
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  const checksum = (10 - (sum % 10)) % 10;
  return checksum === parseInt(digits[digits.length - 1]);
}

const isValidSwedishCompanyCode = (value: string) => {
  if (!value) return true;
  // Swedish organization numbers are 10 digits, often written as XXXXXX-XXXX
  // Also uses Luhn checksum
  // Spotify AB 556703-7485
  const pattern = /^(\d{6}|\d{8})(-|\+)?(\d{4})$/;
  return pattern.test(value) && verifyLuhnChecksum(value);
};

const isValidFinnishPersonalID = (value: string) => {
  if (!value) return true;

  // Remove any whitespace
  value = value.trim().replace(" ", "");

  // Check basic format
  const pattern = /^\d{6}[-+A]\d{3}[\dA-Z]$/;
  if (!pattern.test(value)) return false;

  // Extract date parts
  const day = parseInt(value.substring(0, 2));
  const month = parseInt(value.substring(2, 4));
  const year = parseInt(value.substring(4, 6));
  const centuryMarker = value.charAt(6);

  // Determine full year based on century marker
  let fullYear;
  if (centuryMarker === "+") {
    fullYear = 1800 + year;
  } else if (centuryMarker === "-") {
    fullYear = 1900 + year;
  } else if (centuryMarker === "A") {
    fullYear = 2000 + year;
  } else {
    return false;
  }

  // Validate date
  const date = new Date(fullYear, month - 1, day);
  if (
    date.getFullYear() !== fullYear ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return false;
  }

  // Calculate the checksum
  // The checksum character is determined by
  // DDMMYYIIIC % 31, where DDMMYYIII is treated as a number
  // and C is one of "0123456789ABCDEFHJKLMNPRSTUVWXY"
  const checksumChars = "0123456789ABCDEFHJKLMNPRSTUVWXY";

  // Get the numerical part for checksum calculation
  const numberPart = value.substring(0, 6) + value.substring(7, 10);

  const remainder = parseInt(numberPart) % 31;
  const expectedChar = checksumChars.charAt(remainder);

  // Verify the checksum character
  return expectedChar === value.charAt(10);
};

const isValidFinnishBusinessID = (value: string) => {
  if (!value) return true;

  // Remove any whitespace
  value = value.trim().replace(" ", "");

  // Check basic format: 7 digits, hyphen, 1 check digit
  const pattern = /^\d{7}-\d$/;
  if (!pattern.test(value)) return false;

  // Remove hyphen for calculation
  const digits = value.replace("-", "");

  // Calculate checksum using the Finnish algorithm
  // Weights: 7, 9, 10, 5, 8, 4, 2
  const weights = [7, 9, 10, 5, 8, 4, 2];

  // Calculate sum of products
  let total = 0;
  for (let i = 0; i < 7; i++) {
    total += parseInt(digits.charAt(i)) * weights[i];
  }

  // Calculate check digit
  const remainder = total % 11;
  let checkDigit;
  if (remainder === 0) {
    checkDigit = 0;
  } else if (remainder === 1) {
    // Business IDs with remainder 1 are not valid
    return false;
  } else {
    checkDigit = 11 - remainder;
  }

  // Verify the check digit
  return checkDigit === parseInt(digits.charAt(7));
};

const isValidPolishVATNumber = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  // Polish VAT format is PL followed by 10 digits
  return /^PL\d{10}$/.test(value);
};

const isValidPolishRegistryCode = (value: string) => {
  if (!value) return true; // consider empty values as valid. Use in combination with required
  // Polish Company registry code (KRS) format - 10 digits
  return /^\d{10}$/.test(value);
};

/**
 * VAT number validation patterns for EU member states and other supported countries.
 * Includes: EU countries, CH (Switzerland), NO (Norway), RS (Serbia), GB (United Kingdom), IS (Iceland), TR (Turkey).
 */
const VAT_PATTERNS: Record<string, RegExp> = {
  AT: /^ATU\d{8}$/,
  BE: /^BE[01]\d{9}$/,
  BG: /^BG\d{9,10}$/,
  HR: /^HR\d{11}$/,
  CY: /^CY\d{8}[A-Z]$/,
  CZ: /^CZ\d{8,10}$/,
  DK: /^DK\d{8}$/,
  EE: /^EE\d{9}$/,
  FI: /^FI\d{8}$/,
  FR: /^FR[0-9A-Z]{2}\d{9}$/,
  DE: /^DE\d{9}$/,
  EL: /^EL\d{9}$/,
  GB: /^GB(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
  GR: /^(EL|GR)\d{9}$/,
  HU: /^HU\d{8}$/,
  IE: /^IE(\d{7}[A-Z]{1,2}|\d[A-Z+*]\d{5}[A-Z])$/,
  IS: /^IS\d{6}$/,
  IT: /^IT\d{11}$/,
  LV: /^LV\d{11}$/,
  LT: /^LT(\d{9}|\d{12})$/,
  LU: /^LU\d{8}$/,
  MT: /^MT\d{8}$/,
  NL: /^NL\d{9}B\d{2}$/,
  NO: /^NO\d{9}MVA$/,
  PL: /^PL\d{10}$/,
  PT: /^PT\d{9}$/,
  RO: /^RO\d{2,10}$/,
  RS: /^RS\d{9}$/,
  SK: /^SK\d{10}$/,
  SI: /^SI\d{8}$/,
  ES: /^ES[0-9A-Z]\d{7}[0-9A-Z]$/,
  SE: /^SE\d{12}$/,
  CH: /^CHE\d{9}(TVA|MWST|IVA)?$/,
  TR: /^TR\d{10}$/,
};

/**
 * Validates a VAT number by normalizing it (removing spaces, dots, dashes) and checking
 * against country-specific patterns defined in VAT_PATTERNS.
 *
 * @param vat - The VAT number to validate; empty, null, or undefined values are treated as valid
 * @returns True if the VAT matches the country-specific pattern or is empty/blank, false otherwise
 */
export function isValidVat(vat: string | null | undefined): boolean {
  if (!vat || !vat.trim()) return true;

  const cleanVat = vat.replace(/[\s.-]/g, "").toUpperCase();
  if (cleanVat.length < 4) return false;

  const vatCountryCode = cleanVat.startsWith("CHE")
    ? "CH"
    : cleanVat.substring(0, 2);
  const pattern = VAT_PATTERNS[vatCountryCode];

  return pattern ? pattern.test(cleanVat) : false;
}

/**
 * Extracts the ISO 3166-1 alpha-2 country code from a VAT number, or null when the
 * number doesn't fully match a known country pattern. Greece's "EL" VAT prefix maps
 * to "GR"; Switzerland's "CHE" prefix maps to "CH".
 *
 * Reuses isValidVat so a country is only resolved when the full VAT matches that
 * country's pattern, never from a half-typed prefix.
 *
 * @param vat - The VAT number to resolve
 * @returns The ISO alpha-2 country code, or null when none can be resolved
 */
export function getCountryCodeFromVat(
  vat: string | null | undefined
): string | null {
  if (!isValidVat(vat) || !vat || !vat.trim()) return null;

  const cleanVat = vat.replace(/[\s.-]/g, "").toUpperCase();
  const prefix = cleanVat.startsWith("CHE") ? "CH" : cleanVat.substring(0, 2);

  // VAT prefixes are ISO alpha-2 except Greece, which uses "EL".
  return prefix === "EL" ? "GR" : prefix;
}

/**
 * Normalizes a VAT number for frontend validation and submission.
 *
 * Blank values are returned unchanged. Non-blank values are uppercased and stripped of
 * spaces, dots, and dashes. For Turkish companies, a local 10-digit VAT is converted
 * to the canonical `TR##########` format.
 *
 * @param vat - The VAT number to normalize
 * @param countryCode - Optional ISO country code used for country-specific normalization
 * @returns The normalized VAT number, or the original blank/nullish value
 */
export function normalizeVat(
  vat: string | null | undefined,
  countryCode?: string | null
): string | null | undefined {
  if (!vat || !vat.trim()) return vat;

  const cleanVat = vat.replace(/[\s.-]/g, "").toUpperCase();
  const normalizedCountryCode = countryCode?.toUpperCase();

  if (normalizedCountryCode === "TR" && /^\d{10}$/.test(cleanVat)) {
    return `TR${cleanVat}`;
  }

  return cleanVat;
}

export function useValidators(t: Composer["t"]) {
  const validatorIf = (condition: ComputedRef<boolean>, validator: any) =>
    validators.helpers.withMessage(
      validator.$message,
      (value) => !condition.value || validator.$validator(value)
    );

  // Wrap the validators with the translation function
  const required = validators.helpers.withMessage(
    () => t("validations.required"),
    validators.required
  );

  const email = validators.helpers.withMessage(
    () => t("validations.email"),
    validators.email
  );

  const requiredIf = (condition: any) =>
    validators.helpers.withMessage(
      () => t("validations.required"),
      validators.requiredIf(condition)
    );

  const minLength = (length: number) =>
    validators.helpers.withMessage(
      () => t("validations.minLength", { min: length }),
      validators.minLength(length)
    );
  const minValue = (length: number) =>
    validators.helpers.withMessage(
      () => t("validations.minValue", { min: length }),
      validators.minValue(length)
    );
  const maxLength = (length: number) =>
    validators.helpers.withMessage(
      () => t("validations.maxLength", { max: length }),
      validators.maxLength(length)
    );

  const exactStringLength = (length: number) =>
    validators.helpers.withMessage(
      () => t("validations.exactStringLength", { length }),
      (value: unknown) => typeof value === "string" && value.length === length
    );

  const exactDigitLength = (length: number) =>
    validators.helpers.withMessage(
      () => t("validations.exactDigitLength", { length }),
      (value: unknown) =>
        (typeof value === "number" && value.toString().length === length) ||
        (typeof value === "string" &&
          /^\d+$/.test(value) &&
          value.length === length)
    );

  const estonianPersonalID = validators.helpers.withMessage(
    () => t("validations.estonianPersonalID"),
    isValidEstonianPersonalID
  );

  const latvianPersonalID = validators.helpers.withMessage(
    () => t("validations.latvianPersonalID"),
    isValidLatvianPersonalId
  );

  const estonianCompanyCode = validators.helpers.withMessage(
    () => t("validations.estonianCompanyCode"),
    isValidEstonianCompanyCode
  );

  const estonianDeviceEIC = validators.helpers.withMessage(
    () => t("validations.estonianDeviceEIC"),
    validators.helpers.regex(estonianDeviceEICPattern)
  );

  const estonianVATNumber = validators.helpers.withMessage(
    () => t("validations.estonianVATNumber"),
    isValidEstonianVATNumber
  );
  const notExampleEIC = validators.helpers.withMessage(
    () => t("validations.notExampleEIC"),
    validators.not(validators.helpers.regex(/^38ZEE-12345678-X$/))
  );

  const estonianIDorCompanyCode = validators.helpers.withMessage(
    () => t("validations.estonianIDorCompanyCode"),
    validators.or(estonianCompanyCode, estonianPersonalID)
  );

  const latvianIDorLatvianRegistryCode = validators.helpers.withMessage(
    () => t("validations.latvianIDorRegistryCode"),
    validators.or(isValidLatvianCompanyNumber, latvianPersonalID)
  );

  const latvianVATNumber = validators.helpers.withMessage(
    () => t("validations.validVATNumber"),
    isValidLatvianVATNumber
  );

  const lithuanianCompanyNumber = validators.helpers.withMessage(
    () => t("validations.lithuanianCompanyNumber"),
    isValidLithuanianCompanyNumber
  );

  const lithuanianVATNumber = validators.helpers.withMessage(
    () => t("validations.validVATNumber"),
    isValidLithuanianVATNumber
  );

  const swedishVATNumber = validators.helpers.withMessage(
    () => t("validations.swedishVATNumber"),
    isValidSwedishVATNumber
  );

  const lithuanianPersonalID = validators.helpers.withMessage(
    () => t("validations.lithuanianPersonalID"),
    isValidLithuanianPersonalID
  );

  const estonianYourPersonalIDifID = (your_id: any) =>
    validators.helpers.withMessage(
      () => t("validations.estonianYourPersonalIDifID"),
      validators.or(
        validators.not(estonianPersonalID),
        validators.sameAs(your_id)
      )
    );

  const personalName = validators.helpers.withMessage(
    () => t("validations.personalName"),
    validators.helpers.regex(namePattern)
  );

  const phoneNumber = validators.helpers.withMessage(
    () => t("validations.phoneNumber"),
    validators.helpers.regex(phonePattern)
  );

  const someSubsidies = (subsidy_list: Ref<any[]>) =>
    validators.helpers.withMessage(
      () => t("validations.someSubsidies"),
      (has_subsidies: any) => !has_subsidies || subsidy_list.value.length > 0
    );

  const generatorExists = validators.helpers.withMessage(
    () => t("validations.generatorExists"),
    (value: boolean) => value
  );

  const fileRequired = validators.helpers.withMessage(
    () => t("validations.fileRequired"),
    validators.required
  );

  const reasonableDate = validators.helpers.withMessage(
    () => t("validations.reasonableDate"),
    isReasonableDate
  );

  const iban = validators.helpers.withMessage(
    () => t("validations.iban"),
    isValidIban
  );

  const swedishPersonalID = validators.helpers.withMessage(
    () => t("validations.swedishPersonalID"),
    isValidSwedishPersonalID
  );

  const swedishCompanyCode = validators.helpers.withMessage(
    () => t("validations.swedishCompanyCode"),
    isValidSwedishCompanyCode
  );

  const swedishIDorCompanyCode = validators.helpers.withMessage(
    () => t("validations.swedishIDorCompanyCode"),
    validators.or(swedishCompanyCode, swedishPersonalID)
  );

  const finnishPersonalID = validators.helpers.withMessage(
    () => t("validations.finnishPersonalID"),
    isValidFinnishPersonalID
  );

  const finnishBusinessID = validators.helpers.withMessage(
    () => t("validations.finnishBusinessID"),
    isValidFinnishBusinessID
  );

  const finnishIDorBusinessID = validators.helpers.withMessage(
    () => t("validations.finnishIDorBusinessID"),
    validators.or(finnishBusinessID, finnishPersonalID)
  );

  const polishVATNumber = validators.helpers.withMessage(
    () => t("validation.pl.invalid_vat_number"),
    isValidPolishVATNumber
  );

  const polishRegistryCode = validators.helpers.withMessage(
    () => t("validation.pl.invalid_registry_code"),
    isValidPolishRegistryCode
  );

  const isValidVAT = validators.helpers.withMessage(
    () => t("validations.validVATNumber"),
    isValidVat
  );

  const maxValue = (length: number) =>
    validators.helpers.withMessage(
      () => t("validations.maxValue", { max: length }),
      validators.maxValue(length)
    );

  const requiredMin = (min: number) =>
    validators.helpers.withMessage(
      () => t("validations.required_and_min", { min }),
      validators.and(validators.required, validators.minValue(min))
    );

  const requiredMinMax = (min: number, max: number) =>
    validators.helpers.withMessage(
      () => t("validations.required_and_range", { min, max }),
      validators.and(
        validators.required,
        validators.minValue(min),
        validators.maxValue(max)
      )
    );

  // Validator for integers only (no decimals)
  const integer = validators.helpers.withMessage(
    () => t("validations.integer"),
    validators.integer
  );

  const between = (min: number, max: number) =>
    validators.helpers.withMessage(
      () => t("validations.between", { min, max }),
      validators.between(min, max)
    );

  const selectionRequired = validators.helpers.withMessage(
    () => t("validations.selection_required"),
    (value: unknown[] | null | undefined) => !!value && value.length > 0
  );

  // Enhanced validators for international signup flows
  const validRegistryCode = validators.helpers.withMessage(
    () => t("validations.validRegistryCode"),
    (value: string) => !value || /^[A-Z0-9-]{3,20}$/i.test(value)
  );

  const validAddress = validators.helpers.withMessage(
    () => t("validations.validAddress"),
    (value: string) =>
      !value || (value.trim().length >= 5 && value.trim().length <= 200)
  );

  const validRepresentativeName = validators.helpers.withMessage(
    () => t("validations.validRepresentativeName"),
    (value: string) => {
      if (!value) return true;
      // Use personalName pattern plus length check (2-100 chars)
      return (
        namePattern.test(value) && value.length >= 2 && value.length <= 100
      );
    }
  );

  const validRepresentativePhone = validators.helpers.withMessage(
    () => t("validations.validRepresentativePhone"),
    (value: string) => {
      if (!value) return true;
      // Use phoneNumber pattern plus length check (6-25 chars)
      return (
        phonePattern.test(value) && value.length >= 6 && value.length <= 25
      );
    }
  );

  const productionValue = validators.helpers.withMessage(
    () => t("validations.productionValue"),
    (value: number | string | null | undefined) => {
      if (value === null || value === undefined || value === "") return false;
      const numValue = typeof value === "string" ? parseFloat(value) : value;
      if (isNaN(numValue) || numValue <= 0) return false;
      // Validate whole numbers only
      return Number.isInteger(numValue);
    }
  );

  const notFutureDate = validators.helpers.withMessage(
    () => t("validations.dateCannotBeFuture"),
    (value: string | null | undefined) => {
      if (!value) return true; // Let required handle empty values

      // For month input (YYYY-MM), convert to date
      const recordDate = new Date(value + "-01");
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Reset to start of day

      return recordDate <= now;
    }
  );

  // Registry account number accepted by CounterpartyPicker: empty, plain
  // alphanumerics, or a recognized EECS/Ofgem identifier.
  const acceptableAccountNumber = validators.helpers.withMessage(
    () => t("common.counterparty_picker.account_invalid"),
    isAcceptableAccountNumber
  );

  // Return the wrapped validators
  return {
    required,
    email,
    validatorIf,
    requiredIf,
    minLength,
    min: minValue,
    maxLength,
    max: maxValue,
    exactStringLength,
    exactDigitLength,
    estonianPersonalID,
    estonianDeviceEIC,
    estonianIDorCompanyCode,
    latvianIDorLatvianRegistryCode,
    estonianYourPersonalIDifID,
    estonianVATNumber,
    lithuanianCompanyNumber,
    lithuanianVATNumber,
    lithuanianPersonalID,
    latvianVATNumber,
    notExampleEIC,
    personalName,
    phoneNumber,
    someSubsidies,
    generatorExists,
    fileRequired,
    reasonableDate,
    iban,
    swedishPersonalID,
    swedishIDorCompanyCode,
    requiredMin,
    requiredMinMax,
    swedishVATNumber,
    finnishPersonalID,
    finnishIDorBusinessID,
    isValidVAT,
    integer,
    between,
    selectionRequired,
    // Enhanced validators for international signup flows
    validRegistryCode,
    validAddress,
    validRepresentativeName,
    validRepresentativePhone,
    polishVATNumber,
    polishRegistryCode,
    productionValue,
    notFutureDate,
    acceptableAccountNumber,
  };
}
